import { useState, useEffect, useCallback } from "react";
import { Lock, Upload, Trash2, LogOut, Loader2, Image as ImageIcon, Film, CheckCircle2, Pencil, Check, X as XIcon } from "lucide-react";
import { adminLogin, fetchJobs, uploadJob, deleteJob, renameJob, mediaUrl } from "../lib/jobsApi";

const KEY = "add_admin_pw";

export const Admin = () => {
  const [pw, setPw] = useState(sessionStorage.getItem(KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loginErr, setLoginErr] = useState("");

  const [jobs, setJobs] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const load = useCallback(() => { fetchJobs().then(setJobs).catch(() => {}); }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(KEY);
    if (saved) adminLogin(saved).then((ok) => {
      if (ok) { setAuthed(true); setPw(saved); }
      else { sessionStorage.removeItem(KEY); setPw(""); }
    });
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setChecking(true); setLoginErr("");
    const ok = await adminLogin(pw);
    setChecking(false);
    if (ok) { sessionStorage.setItem(KEY, pw); setAuthed(true); }
    else setLoginErr("Incorrect password. Please try again.");
  };

  const logout = () => { sessionStorage.removeItem(KEY); setAuthed(false); setPw(""); };

  const submitUpload = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!file) { setErr("Please choose a photo or video."); return; }
    setBusy(true);
    try {
      await uploadJob(pw, title, file);
      setMsg("Uploaded successfully.");
      setTitle(""); setFile(null);
      document.getElementById("file-input").value = "";
      load();
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try { await deleteJob(pw, id); setJobs((j) => j.filter((x) => x.id !== id)); }
    catch { setErr("Delete failed."); }
  };

  const startEdit = (j) => { setEditingId(j.id); setEditTitle(j.title); };
  const cancelEdit = () => { setEditingId(null); setEditTitle(""); };
  const saveEdit = async (id) => {
    try {
      const updated = await renameJob(pw, id, editTitle);
      setJobs((j) => j.map((x) => (x.id === id ? { ...x, title: updated.title } : x)));
      cancelEdit();
    } catch { setErr("Rename failed."); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-brand-brown grain flex items-center justify-center px-5">
        <form onSubmit={submitLogin} data-testid="admin-login-form" className="w-full max-w-sm bg-brand-surface rounded-3xl p-8 shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-brand-olive flex items-center justify-center text-brand-bg mx-auto mb-5">
            <Lock size={26} />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-brand-brown text-center">Admin Access</h1>
          <p className="text-brand-muted text-sm text-center mt-1 mb-6">Enter your password to manage Recent Jobs.</p>
          <input
            type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="Password" data-testid="admin-password-input" autoFocus
            className="w-full rounded-xl border border-brand-line px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-olive"
          />
          {loginErr && <p data-testid="login-error" className="text-brand-red text-sm mt-2">{loginErr}</p>}
          <button type="submit" disabled={checking} data-testid="admin-login-button"
            className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-brand-olive px-5 py-3 font-semibold text-brand-bg hover:bg-brand-oliveDark transition-colors disabled:opacity-60">
            {checking ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />} Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg py-10 px-5">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-brown">Recent Jobs Manager</h1>
            <p className="text-brand-muted text-sm">Add or remove photos & videos shown on your Recent Jobs page.</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-medium text-brand-muted hover:text-brand-brown">
            <LogOut size={16} /> Log out
          </button>
        </div>

        <form onSubmit={submitUpload} data-testid="upload-form" className="bg-brand-surface rounded-2xl border border-brand-line p-6 mb-8">
          <p className="font-display font-bold text-lg text-brand-brown mb-4">Add new item</p>
          <label className="block text-sm font-medium text-brand-ink mb-1">Name / title</label>
          <input
            value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Fitted Oak Kitchen"
            data-testid="upload-title" maxLength={80}
            className="w-full rounded-xl border border-brand-line px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-brand-olive"
          />
          <label className="block text-sm font-medium text-brand-ink mb-1">Photo or short video</label>
          <input
            id="file-input" type="file" accept="image/*,video/*"
            onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file"
            className="w-full text-sm text-brand-muted file:mr-3 file:rounded-lg file:border-0 file:bg-brand-brown file:px-4 file:py-2 file:text-brand-bg file:font-medium mb-1"
          />
          <p className="text-xs text-brand-muted mb-4">Max 60MB. Videos should be short clips.</p>
          <button type="submit" disabled={busy} data-testid="upload-button"
            className="inline-flex items-center gap-2 rounded-xl bg-brand-olive px-6 py-3 font-semibold text-brand-bg hover:bg-brand-oliveDark transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} {busy ? "Uploading…" : "Upload"}
          </button>
          {msg && <p data-testid="upload-success" className="text-brand-olive text-sm mt-3 inline-flex items-center gap-1"><CheckCircle2 size={15} /> {msg}</p>}
          {err && <p data-testid="upload-error" className="text-brand-red text-sm mt-3">{err}</p>}
        </form>

        <p className="font-display font-bold text-lg text-brand-brown mb-4">Your uploads ({jobs.length})</p>
        {jobs.length === 0 ? (
          <p className="text-brand-muted text-sm bg-brand-surface rounded-2xl border border-brand-line p-6">No uploads yet. Your added photos and videos will appear here.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="admin-jobs-grid">
            {jobs.map((j) => (
              <div key={j.id} data-testid={`admin-job-${j.id}`} className="relative rounded-xl overflow-hidden ring-1 ring-brand-line bg-brand-surface">
                <div className="aspect-square bg-brand-brown/5">
                  {j.media_type === "video"
                    ? <video src={mediaUrl(j.id)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    : <img src={mediaUrl(j.id)} alt={j.title} className="w-full h-full object-cover" />}
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  {editingId === j.id ? (
                    <>
                      <input
                        value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                        data-testid={`edit-input-${j.id}`} maxLength={80} autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(j.id); if (e.key === "Escape") cancelEdit(); }}
                        className="min-w-0 flex-1 text-sm rounded-lg border border-brand-line px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-olive"
                      />
                      <button onClick={() => saveEdit(j.id)} data-testid={`save-${j.id}`} className="shrink-0 text-brand-olive hover:scale-110 transition-transform" aria-label="Save"><Check size={18} /></button>
                      <button onClick={cancelEdit} data-testid={`cancel-${j.id}`} className="shrink-0 text-brand-muted hover:scale-110 transition-transform" aria-label="Cancel"><XIcon size={17} /></button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-brand-ink truncate inline-flex items-center gap-1.5">
                        {j.media_type === "video" ? <Film size={14} className="text-brand-olive shrink-0" /> : <ImageIcon size={14} className="text-brand-olive shrink-0" />}
                        {j.title}
                      </span>
                      <span className="shrink-0 flex items-center gap-2">
                        <button onClick={() => startEdit(j)} data-testid={`edit-${j.id}`} className="text-brand-muted hover:text-brand-brown hover:scale-110 transition-all" aria-label="Rename"><Pencil size={16} /></button>
                        <button onClick={() => remove(j.id)} data-testid={`delete-${j.id}`} className="text-brand-red hover:scale-110 transition-transform" aria-label="Delete"><Trash2 size={17} /></button>
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
